import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { api } from "../../lib/api.js";
import type { ReportDetail } from "./types.js";
import { useT } from "../../i18n/index.js";

const s = {
  page: {
    maxWidth: "960px",
    margin: "0 auto",
    padding: "var(--space-lg) var(--space-md) var(--space-xl)",
    color: "var(--text)",
  },
  heading: { margin: "0.5rem 0 0.25rem", fontSize: "var(--text-xl)", fontWeight: 700 },
  meta: { color: "var(--text-dim)", fontSize: "var(--text-sm)", margin: "0 0 2rem" },
  section: { marginBottom: "2.5rem" },
  sectionTitle: { fontSize: "var(--text-lg)", fontWeight: 700, margin: "0 0 1rem", color: "var(--brand)" },
  tableWrap: {
    overflowX: "auto" as const,
    borderRadius: "var(--radius-md)",
    border: "1px solid var(--border)",
  },
  table: { width: "100%", borderCollapse: "collapse" as const },
  th: {
    textAlign: "left" as const,
    padding: "0.6rem 1rem",
    background: "var(--bg-deep)",
    color: "var(--text-mid)",
    fontSize: "var(--text-xs)",
    textTransform: "uppercase" as const,
    letterSpacing: "0.05em",
    borderBottom: "2px solid var(--border)",
  },
  td: {
    padding: "0.6rem 1rem",
    borderBottom: "1px solid var(--border)",
    fontSize: "var(--text-sm)",
    color: "var(--text)",
  },
  rank: (rank: number) => ({
    fontWeight: 800,
    color: rank === 1
      ? "oklch(70% 0.18 80)"
      : rank === 2
      ? "var(--text-mid)"
      : rank === 3
      ? "oklch(60% 0.14 50)"
      : "var(--text)",
  }),
  csvBtn: {
    display: "inline-block",
    marginBottom: "1.5rem",
    padding: "0.5rem 1.25rem",
    background: "var(--surface)",
    border: "1.5px solid var(--border)",
    borderRadius: "var(--radius-md)",
    fontWeight: 600,
    fontSize: "var(--text-sm)",
    textDecoration: "none",
  },
  summaryGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
    gap: "1rem",
    marginBottom: "2rem",
  },
  summaryCard: {
    background: "var(--surface)",
    borderRadius: "var(--radius-md)",
    padding: "1rem 1.25rem",
    border: "1px solid var(--border)",
    boxShadow: "var(--shadow-sm)",
  },
  summaryLabel: {
    fontSize: "var(--text-xs)",
    color: "var(--text-dim)",
    textTransform: "uppercase" as const,
    letterSpacing: "0.05em",
  },
  summaryValue: { fontSize: "var(--text-lg)", fontWeight: 700, marginTop: "0.25rem" },
  error: { color: "var(--error)" },
};

function formatDate(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleString();
}

function pct(num: number, denom: number): string {
  if (denom === 0) return "0%";
  return `${Math.round((num / denom) * 100)}%`;
}

export function ReportDetail() {
  const t = useT();
  const { sessionId } = useParams<{ sessionId: string }>();
  const [report, setReport] = useState<ReportDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!sessionId) return;
    api
      .get<{ report: ReportDetail }>(`/reports/${sessionId}`)
      .then((res) => setReport(res.report))
      .catch((err: unknown) => setError(err instanceof Error ? err.message : t("reports.loadingReport")))
      .finally(() => setLoading(false));
  }, [sessionId, t]);

  if (loading) return <div style={s.page}><p>{t("reports.loadingReport")}</p></div>;
  if (error) return <div style={s.page}><p style={s.error}>{error}</p></div>;
  if (!report) return <div style={s.page}><p>{t("reports.notFound")}</p></div>;

  const { summary, participants, questions } = report;

  return (
    <div style={s.page}>
      <h1 style={s.heading}>{summary.quizTitle}</h1>
      <p style={s.meta}>{t("reports.endedAt", { date: formatDate(summary.endedAt) })}</p>

      <a
        href={`/api/reports/${sessionId}/csv`}
        style={s.csvBtn}
        download
        aria-label={t("reports.downloadCsvExport")}
      >
        {t("reports.downloadCsv")}
      </a>

      <div style={s.summaryGrid} aria-label={t("reports.sessionSummary")}>
        <div style={s.summaryCard}>
          <div style={s.summaryLabel}>{t("reports.players")}</div>
          <div style={s.summaryValue}>{summary.playerCount}</div>
        </div>
        <div style={s.summaryCard}>
          <div style={s.summaryLabel}>{t("reports.questions")}</div>
          <div style={s.summaryValue}>{summary.questionCount}</div>
        </div>
        <div style={s.summaryCard}>
          <div style={s.summaryLabel}>{t("reports.status")}</div>
          <div style={s.summaryValue}>{summary.phase}</div>
        </div>
      </div>

      <section style={s.section} aria-labelledby="participants-heading">
        <h2 id="participants-heading" style={s.sectionTitle}>{t("reports.participants")}</h2>
        <div style={s.tableWrap}>
          <table style={s.table} aria-label={t("reports.participantRankings")}>
            <thead>
              <tr>
                <th style={s.th}>{t("reports.rank")}</th>
                <th style={s.th}>{t("reports.nickname")}</th>
                <th style={s.th}>{t("reports.score")}</th>
                <th style={s.th}>{t("reports.correct")}</th>
                <th style={s.th}>{t("reports.accuracy")}</th>
              </tr>
            </thead>
            <tbody>
              {participants.map((p) => (
                <tr key={p.playerId}>
                  <td style={{ ...s.td, ...s.rank(p.rank) }}>#{p.rank}</td>
                  <td style={s.td}>{p.nickname}</td>
                  <td style={s.td}>{p.totalScore}</td>
                  <td style={s.td}>{p.correctCount} / {p.totalAnswered}</td>
                  <td style={s.td}>{pct(p.correctCount, p.totalAnswered)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section style={s.section} aria-labelledby="questions-heading">
        <h2 id="questions-heading" style={s.sectionTitle}>{t("reports.questionAnalytics")}</h2>
        <div style={s.tableWrap}>
          <table style={s.table} aria-label={t("reports.questionAnalytics")}>
            <thead>
              <tr>
                <th style={s.th}>#</th>
                <th style={s.th}>{t("reports.question")}</th>
                <th style={s.th}>{t("reports.totalAnswers")}</th>
                <th style={s.th}>{t("reports.correct")}</th>
                <th style={s.th}>{t("reports.accuracy")}</th>
              </tr>
            </thead>
            <tbody>
              {questions.map((q) => (
                <tr key={q.questionRunId}>
                  <td style={s.td}>{q.orderIndex + 1}</td>
                  <td style={s.td}>{q.prompt ?? t("reports.noPrompt")}</td>
                  <td style={s.td}>{q.totalAnswers}</td>
                  <td style={s.td}>{q.correctAnswers}</td>
                  <td style={s.td}>{pct(q.correctAnswers, q.totalAnswers)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
